import React, { FC, useMemo, useState } from 'react'
import { useSelector } from 'react-redux';
import { getUserInfo } from '../../../../store/ducks/userInfo';
import IconSettings from '@salesforce/design-system-react/components/icon-settings';
import { InventoryAddPartsProps } from './InventoryAddParts.types';
import useSnackBar from '../../../../util/customHooks/useSnackBar';
import SnackBar from '../../../Shared/SnackBar';
import Modal from '@salesforce/design-system-react/components/modal';
import Spinner from '@salesforce/design-system-react/components/spinner';
import Button from '@salesforce/design-system-react/components/button';
import styles from './styles.module.scss';
import CartComponent from '../../../Usage/UsageDetails/UsageTabs/Parts/CartComponent';
import { ACCOUNT_TYPE_RETURN } from '../../../../util/constants';
import useSaveParts from './hook/useSaveParts';
import SearchBar from '../../../Usage/UsageDetails/UsageTabs/Parts/AddParts/SearchBar/SearchBar';
import InventorySection from '../../../Usage/UsageDetails/UsageTabs/Parts/AddParts/InventorySection/InventorySection';
import useParts, { INVENTORY_CHANNELS } from '../../../Usage/UsageDetails/UsageTabs/Parts/AddParts/useParts';

const InventoryAddParts: FC<InventoryAddPartsProps> = ({
    isOpen,
    handleClose,
    inventoryReturnDetail,
    returnType,
    count = 0,
}) => {
    // #region Hooks
    const userInfo = useSelector(getUserInfo);
    const isOps = userInfo && userInfo?.personas?.toLowerCase().indexOf('op') > -1;
    const isCSR = userInfo && userInfo?.personas?.toLowerCase().indexOf('csr') > -1;
    const isRepstockAccount = ACCOUNT_TYPE_RETURN.some(v =>
        inventoryReturnDetail?.accountNumber?.includes(v)
    );
    const { open, notification, openSnackBar } = useSnackBar();
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const catalogRuleMap: Record<string, string> = {
        'Pickup Return': 'Allow Add from Catalog on Pickup Return',
        'Inventory Return': 'Allow Add from Catalog on Standard Return',
    };
    const catalogVisibility = useMemo(() => {
        if (!userInfo?.businessRules || !inventoryReturnDetail?.type) return false;
        const ruleKey = catalogRuleMap[inventoryReturnDetail.type];
        if (!ruleKey) return false;
        const rule = userInfo.businessRules.find((br: any) => br.rules === ruleKey);
        return rule?.isRequired ?? false;
    }, [userInfo, inventoryReturnDetail]);

    const catalogRules = catalogVisibility || isOps || isCSR;
    const {
        searchText,
        searchInventory,
        inventoryCount,
        repstockInventory,
        loadingRepstockInventory,
        customerInventory,
        loadingCustomerInventory,
        handleInventoryCount,
        productGroups,
        loadingProductGroups,
        selectedGroup,
        setSelectedGroup,
        loadingCatalogInventory,
        catalogInventory,
        handleCatalogCount,
        inventoryPartsToAdd,
        catalogPartsToAdd,
    } = useParts({
        usageDetails: inventoryReturnDetail,
        shouldLoadCatalogByDefault: false,
        shouldSkipCustomer: isRepstockAccount,
        shouldSkipRepstock: !isRepstockAccount,
        initialPartsCount: count,
        userInfo, openSnackBar
    });

    const { onAddClick, loadingLineItems } = useSaveParts({
        setIsSaving,
        inventoryReturnDetail,
        isRepstockAccount,
        inventoryPartsToAdd,
        catalogPartsToAdd,
        returnType,
        handleClose,
        openSnackBar,
    });
    // #endregion Hooks

    return (
        <IconSettings iconPath="/icons">
            <SnackBar open={open} notification={notification} />
            <Modal
                ariaHideApp={false}
                isOpen={isOpen}
                footer={[
                    <div className="usage-modal-footer">
                        {(isSaving ||
                            loadingCustomerInventory ||
                            loadingRepstockInventory ||
                            loadingCatalogInventory) && <Spinner size="small" />}
                        <Button
                            label="Cancel"
                            onClick={() => {
                                handleClose();
                            }}
                        />
                        <Button label="Add" variant="brand" onClick={onAddClick} />
                    </div>,
                ]}
                onRequestClose={() => {
                    handleClose();
                }}
                dismissOnClickOutside={false}
            >
                <div className={styles.cartComponentContainer}>
                    <p className={styles.title}>Add Parts</p>
                    <div className={styles.cartComponentAlign}>
                        <CartComponent
                            count={inventoryCount}
                            color="black"
                            textColor="white" />
                    </div>
                </div>
                <div className={styles.modalInnerContainer}>
                    {loadingLineItems && <Spinner size="large" />}
                    <SearchBar
                        searchInventory={searchInventory}
                        loading={loadingRepstockInventory || loadingCustomerInventory}
                        productGroups={productGroups}
                        loadingProductGroups={loadingProductGroups}
                        selectedGroup={selectedGroup}
                        setSelectedGroup={setSelectedGroup}
                    />
                    <div className={styles.sectionsContainer}>
                        {isRepstockAccount && (<InventorySection
                            title="Repstock"
                            searching={searchText.length > 2}
                            inventoryType={INVENTORY_CHANNELS.REPSTOCK}
                            inventory={repstockInventory}
                            loading={loadingRepstockInventory}
                            handleInventoryCounter={handleInventoryCount}
                            accountNumber={inventoryReturnDetail?.accountNumber}
                        />)}
                        {!isRepstockAccount && (<InventorySection
                            title="Customer"
                            searching={searchText.length > 2}
                            inventoryType={INVENTORY_CHANNELS.CUSTOMER}
                            inventory={customerInventory}
                            loading={loadingCustomerInventory}
                            handleInventoryCounter={handleInventoryCount}
                        />)}
                        {(catalogRules) && (<InventorySection
                            title="Catalog"
                            searching={searchText.length > 2}
                            inventoryType={INVENTORY_CHANNELS.CATALOG}
                            inventory={catalogInventory}
                            loading={loadingCatalogInventory}
                            handleCatalogCounter={handleCatalogCount}
                            isInventoryReturn
                        />)}
                    </div>
                </div>
            </Modal>
        </IconSettings>
    )
}

export default InventoryAddParts